import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ImageCropModal from "./ImageCropModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_PALETTE = [
  "#E0F2FE", "#FFE8E4", "#FFF4E0", "#F0FFE8",
  "#FFE8F4", "#F5EFE0", "#EBF5FF", "#FFEDD5",
];

const ProductFormModal = ({ isOpen, onClose, onSuccess, categories, editProduct, preselectedBrand, keepOpenAfterSave = false }) => {
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const [brands, setBrands] = useState([]);
  // Active tier tab: "A_plus" (regular price tab) | "B_plus" (wholesale price tab).
  // Both tabs share the SAME form state — they just show / hide the wholesale
  // price input. So the operator can build the product once and choose which
  // pricing tiers to fill.
  const [activeTier, setActiveTier] = useState("A_plus");
  const [form, setForm] = useState({
    name: "",
    name_ar: "",
    price: "",
    cost_price: "",
    wholesale_price: "",
    c_plus_price: "",
    category: "",
    stock: "0",
    color: "#E0F2FE",
    is_best_seller: false,
    is_offer: false,
    is_win: false,
    excludes_rewards: true,
    featured_offer_slot: null,
    image_path: null,
    image_path2: null,
    brand_id: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreview2, setImagePreview2] = useState(null);
  const [pendingCrop, setPendingCrop] = useState(null); // {file, slot}
  const [uploading, setUploading] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset form ONLY when the modal opens or when switching between edit/create.
  // Without this guard, parent re-renders (e.g. categories refetch) would wipe the form.
  const lastInitRef = useRef({ isOpen: false, editId: null });
  useEffect(() => {
    const editId = editProduct?.id || null;
    const last = lastInitRef.current;
    // Only initialize when:
    //  - modal just opened, OR
    //  - we switched to a different product to edit
    if (!isOpen) {
      lastInitRef.current = { isOpen: false, editId: null };
      return;
    }
    if (last.isOpen && last.editId === editId) return; // already initialized for this session
    lastInitRef.current = { isOpen: true, editId };

    if (editProduct) {
      setForm({
        name: editProduct.name || "",
        name_ar: editProduct.name_ar || "",
        price: String(editProduct.price ?? ""),
        cost_price: String(editProduct.cost_price ?? ""),
        wholesale_price: editProduct.wholesale_price != null ? String(editProduct.wholesale_price) : "",
        c_plus_price: editProduct.c_plus_price != null ? String(editProduct.c_plus_price) : "",
        category: editProduct.category || "",
        stock: String(editProduct.stock ?? 0),
        color: editProduct.color || "#E0F2FE",
        is_best_seller: !!editProduct.is_best_seller,
        is_offer: !!editProduct.is_offer,
        is_win: !!editProduct.is_win,
        excludes_rewards: !!editProduct.excludes_rewards,
        featured_offer_slot: editProduct.featured_offer_slot || null,
        image_path: editProduct.image_path || null,
        image_path2: editProduct.image_path2 || null,
        brand_id: editProduct.brand_id || "",
      });
      // Open on B+ tab automatically if a wholesale price already exists
      setActiveTier(editProduct.wholesale_price != null && editProduct.wholesale_price > 0 ? "B_plus" : "A_plus");
      setImagePreview(editProduct.image_path ? `${API}/files/${editProduct.image_path}` : null);
      setImagePreview2(editProduct.image_path2 ? `${API}/files/${editProduct.image_path2}` : null);
    } else {
      // Determine the category for a brand-new product. Multi-category brands
      // have `category: null` (categories[] holds the list), so we must fall
      // back to that array's first item, NOT the global categories[0].
      const initialCategory =
        preselectedBrand?.category
        || preselectedBrand?.categories?.[0]
        || categories[0]?.name
        || "";
      setForm({
        name: "",
        name_ar: "",
        price: "",
        cost_price: "",
        wholesale_price: "",
        c_plus_price: "",
        category: initialCategory,
        stock: "0",
        color: "#E0F2FE",
        is_best_seller: false,
        is_offer: false,
        is_win: false,
        excludes_rewards: true,
        featured_offer_slot: null,
        image_path: null,
        image_path2: null,
        brand_id: preselectedBrand?.id || "",
      });
      setActiveTier("A_plus");
      setImagePreview(null);
      setImagePreview2(null);
    }
  }, [isOpen, editProduct, categories, preselectedBrand]);

  // Fetch brands for the currently-selected category
  useEffect(() => {
    if (!form.category) { setBrands([]); return; }
    // Eagerly seed with the preselected brand so the dropdown displays it
    // immediately on first render (avoids a 200ms blank-select flash).
    if (preselectedBrand?.id && preselectedBrand?.category === form.category) {
      setBrands((prev) => {
        if (prev.find((b) => b.id === preselectedBrand.id)) return prev;
        return [
          {
            id: preselectedBrand.id,
            name_ar: preselectedBrand.name_ar || "...",
            category: preselectedBrand.category,
            product_count: preselectedBrand.product_count ?? 0,
          },
          ...prev,
        ];
      });
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/brands`, { params: { category: form.category } });
        if (!cancelled) setBrands(data || []);
      } catch { if (!cancelled) setBrands([]); }
    })();
    return () => { cancelled = true; };
  }, [form.category, preselectedBrand]);

  const uploadImage = async (file, slot /* 1 or 2 */) => {
    if (!file.type.startsWith("image/")) {
      toast.error("الرجاء اختيار صورة فقط");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("الصورة يجب أن تكون أقل من 5 ميغابايت");
      return;
    }
    const setBusy = slot === 1 ? setUploading : setUploading2;
    setBusy(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (slot === 1) {
        setForm((f) => ({ ...f, image_path: data.path }));
        setImagePreview(URL.createObjectURL(file));
      } else {
        setForm((f) => ({ ...f, image_path2: data.path }));
        setImagePreview2(URL.createObjectURL(file));
      }
      toast.success(slot === 1 ? "تم رفع الصورة الأولى" : "تم رفع الصورة الثانية");
    } catch (err) {
      const msg = err.response?.data?.detail || "فشل رفع الصورة";
      toast.error(typeof msg === "string" ? msg : "فشل رفع الصورة");
    } finally {
      setBusy(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPendingCrop({ file, slot: 1 });
  };
  const handleImageChange2 = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPendingCrop({ file, slot: 2 });
  };

  const handleCropConfirm = async (croppedFile) => {
    if (!pendingCrop) return;
    await uploadImage(croppedFile, pendingCrop.slot);
    setPendingCrop(null);
  };

  const removeImage = (slot) => {
    if (slot === 1) {
      setForm((f) => ({ ...f, image_path: null }));
      setImagePreview(null);
    } else {
      setForm((f) => ({ ...f, image_path2: null }));
      setImagePreview2(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name_ar || !form.price || !form.category) {
      toast.error("الرجاء تعبئة الحقول الأساسية");
      return;
    }

    const price = parseFloat(form.price);
    const cost_price = parseFloat(form.cost_price || "0") || 0;
    // Wholesale price is optional. Empty string → null (clears any old value
    // on edit, so B+ falls back to the regular price).
    const wholesale_price = form.wholesale_price !== "" && form.wholesale_price != null
      ? parseFloat(form.wholesale_price)
      : null;
    if (wholesale_price !== null && (isNaN(wholesale_price) || wholesale_price < 0)) {
      toast.error("سعر الجملة (B+) غير صحيح");
      return;
    }
    // C+ price — same semantics as B+: empty → null clears, must be ≥ 0.
    const c_plus_price = form.c_plus_price !== "" && form.c_plus_price != null
      ? parseFloat(form.c_plus_price)
      : null;
    if (c_plus_price !== null && (isNaN(c_plus_price) || c_plus_price < 0)) {
      toast.error("سعر C+ غير صحيح");
      return;
    }
    const stock = parseInt(form.stock, 10);
    if (isNaN(price) || price <= 0) {
      toast.error("السعر غير صحيح");
      return;
    }
    if (cost_price < 0) {
      toast.error("سعر الشراء غير صحيح");
      return;
    }
    if (isNaN(stock) || stock < 0) {
      toast.error("الكمية غير صحيحة");
      return;
    }

    const payload = {
      name: form.name || form.name_ar,
      name_ar: form.name_ar,
      price,
      cost_price,
      wholesale_price,
      c_plus_price,
      category: form.category,
      stock,
      color: form.color,
      is_best_seller: form.is_best_seller,
      is_offer: form.is_offer,
      is_win: form.is_win,
      excludes_rewards: form.excludes_rewards,
      featured_offer_slot: form.is_offer ? (form.featured_offer_slot || null) : null,
      image_path: form.image_path,
      image_path2: form.image_path2,
      brand_id: form.brand_id || null,
    };

    setSubmitting(true);
    try {
      if (editProduct) {
        await axios.patch(`${API}/admin/products/${editProduct.id}`, payload);
        toast.success("تم تحديث المنتج");
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success("تم إضافة المنتج");
      }
      onSuccess();
      if (keepOpenAfterSave && !editProduct) {
        // Bulk-add mode: keep the modal open and reset the form so the
        // operator can immediately type the next product without re-opening.
        // Preselected brand + category are preserved. Only closes on ✕.
        const initialCategory =
          preselectedBrand?.category
          || preselectedBrand?.categories?.[0]
          || categories[0]?.name
          || "";
        setForm({
          name: "",
          name_ar: "",
          price: "",
          cost_price: "",
          wholesale_price: "",
          c_plus_price: "",
          category: initialCategory,
          stock: "0",
          color: "#E0F2FE",
          is_best_seller: false,
          is_offer: false,
          is_win: false,
          excludes_rewards: true,
          featured_offer_slot: null,
          image_path: null,
          image_path2: null,
          brand_id: preselectedBrand?.id || "",
        });
        setActiveTier("A_plus");
        setImagePreview(null);
        setImagePreview2(null);
        // Bypass the useEffect guard so future opens still work correctly.
        lastInitRef.current = { isOpen: true, editId: null };
      } else {
        onClose();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ";
      toast.error(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
        onClick={onClose}
        data-testid="product-form-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="product-form-modal"
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <div className="sticky top-0 bg-white border-b border-[#E4EBE6] px-6 py-4 flex justify-between items-center z-10">
            <h2 className="font-heading font-bold text-xl text-[#111814]">
              {editProduct ? "تعديل منتج" : "إضافة منتج جديد"}
            </h2>
            <button
              onClick={onClose}
              data-testid="form-close"
              className="p-2 hover:bg-[#F3F6F4] rounded-full"
            >
              <X className="w-5 h-5 text-[#2D4A3E]" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Images (two slots) */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                صور المنتج (الصورتان ستتبدّلان كل ثانية في عرض الزبون)
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Image 1 */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#BAE6FD] rounded-2xl p-2 hover:bg-[#E0F2FE] cursor-pointer transition-colors text-center relative"
                  data-testid="upload-area"
                >
                  {imagePreview ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="preview"
                        className="mx-auto h-28 w-full object-contain rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeImage(1); }}
                        data-testid="remove-image-1"
                        className="absolute top-1 left-1 bg-[#E85D45] text-white rounded-full p-1 hover:bg-[#c14a36]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <div className="py-6">
                      <ImageIcon className="w-8 h-8 text-[#5C6E64] mx-auto mb-1" />
                      <p className="text-xs text-[#5C6E64]">الصورة الأولى</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    data-testid="upload-input"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
                      <Loader2 className="w-5 h-5 animate-spin text-[#1E3A5F]" />
                    </div>
                  )}
                  <p className="text-[10px] text-[#5C6E64] mt-1">الصورة 1</p>
                </div>

                {/* Image 2 */}
                <div
                  onClick={() => fileInputRef2.current?.click()}
                  className="border-2 border-dashed border-[#7ED321]/50 rounded-2xl p-2 hover:bg-[#F0FFE8] cursor-pointer transition-colors text-center relative"
                  data-testid="upload-area-2"
                >
                  {imagePreview2 ? (
                    <>
                      <img
                        src={imagePreview2}
                        alt="preview 2"
                        className="mx-auto h-28 w-full object-contain rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeImage(2); }}
                        data-testid="remove-image-2"
                        className="absolute top-1 left-1 bg-[#E85D45] text-white rounded-full p-1 hover:bg-[#c14a36]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <div className="py-6">
                      <ImageIcon className="w-8 h-8 text-[#7ED321] mx-auto mb-1" />
                      <p className="text-xs text-[#5C6E64]">الصورة الثانية (اختياري)</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef2}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange2}
                    className="hidden"
                    data-testid="upload-input-2"
                  />
                  {uploading2 && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
                      <Loader2 className="w-5 h-5 animate-spin text-[#1E3A5F]" />
                    </div>
                  )}
                  <p className="text-[10px] text-[#5C6E64] mt-1">الصورة 2</p>
                </div>
              </div>
            </div>

            {/* Name AR */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                اسم المنتج (عربي) *
              </label>
              <input
                type="text"
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                data-testid="form-name-ar"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                placeholder="مثلاً: حليب طازج"
                required
              />
            </div>

            {/* Name EN */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                الاسم (إنجليزي)
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="form-name"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                placeholder="Fresh Milk"
              />
            </div>

            {/* Tier tabs — A+ shows the normal selling price the customer sees;
                B+ adds a "سعر الجملة" input shown ONLY to B+ customers. */}
            <div className="bg-gradient-to-r from-[#E0F2FE] to-[#F0F9FF] rounded-2xl p-3 border-2 border-[#3B82F6]/30"
              data-testid="product-tier-tabs">
              <div className="flex items-center justify-center gap-2 mb-3">
                <button type="button" onClick={() => setActiveTier("A_plus")}
                  data-testid="tier-tab-A_plus"
                  className={`flex items-center justify-center rounded-full font-black text-sm w-16 h-16 transition-all ${
                    activeTier === "A_plus"
                      ? "bg-[#3B82F6] text-white shadow-lg scale-110 ring-4 ring-[#3B82F6]/20"
                      : "bg-white text-[#3B82F6] border-2 border-[#3B82F6]/30 hover:border-[#3B82F6]"
                  }`}
                >
                  A+
                </button>
                <span className="text-[#5C6E64] text-xs px-1">|</span>
                <button type="button" onClick={() => setActiveTier("C_plus")}
                  data-testid="tier-tab-C_plus"
                  className={`flex items-center justify-center rounded-full font-black text-sm w-16 h-16 transition-all ${
                    activeTier === "C_plus"
                      ? "bg-slate-700 text-white shadow-lg scale-110 ring-4 ring-slate-700/20"
                      : "bg-white text-slate-700 border-2 border-slate-500/30 hover:border-slate-700"
                  }`}
                >
                  C+
                </button>
                <span className="text-[#5C6E64] text-xs px-1">|</span>
                <button type="button" onClick={() => setActiveTier("B_plus")}
                  data-testid="tier-tab-B_plus"
                  className={`flex items-center justify-center rounded-full font-black text-sm w-16 h-16 transition-all ${
                    activeTier === "B_plus"
                      ? "bg-[#7C3AED] text-white shadow-lg scale-110 ring-4 ring-[#7C3AED]/20"
                      : "bg-white text-[#7C3AED] border-2 border-[#7C3AED]/30 hover:border-[#7C3AED]"
                  }`}
                >
                  B+
                </button>
              </div>

              {activeTier === "A_plus" ? (
                <p className="text-[11px] text-center text-[#1E3A5F]" data-testid="tier-hint-A_plus">
                  ✅ <span className="font-bold">الزبون العادي</span> — يرى سعر البيع الموجود أسفله. لا حقول إضافية.
                </p>
              ) : activeTier === "C_plus" ? (
                <div data-testid="tier-panel-C_plus">
                  <label className="block text-xs font-bold mb-1.5 text-slate-700">
                    سعر C+ (وسط بين العادي والجملة)
                    {!form.c_plus_price && (
                      <span className="text-slate-500 text-[10px] mr-1 font-normal">— اتركه فارغ ليرى زبائن C+ نفس سعر A+</span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.c_plus_price}
                    onChange={(e) => setForm({ ...form, c_plus_price: e.target.value })}
                    placeholder="0 (=بنفس سعر A+)"
                    data-testid="cplus-price-input"
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500/40 focus:border-slate-700 text-slate-900 font-bold text-base"
                    dir="ltr"
                  />
                  {form.c_plus_price && form.price && Number(form.c_plus_price) > 0 && Number(form.price) > 0 && (
                    <div className="mt-2 bg-slate-100 border border-slate-300 rounded-xl px-3 py-2 text-[11px] text-slate-800 flex items-center justify-between">
                      <span>وفر مقارنة بـ A+:</span>
                      <span className="font-black tabular-nums" dir="ltr">
                        {(Number(form.price) - Number(form.c_plus_price)).toLocaleString("en-US")} د.ع
                        <span className="text-[10px] mr-1 opacity-70">
                          ({((Number(form.price) - Number(form.c_plus_price)) / Number(form.price) * 100).toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="tier-panel-B_plus">
                  <p className="text-[11px] text-center text-[#5b21b6] mb-2">
                    💎 <span className="font-bold">الزبون المميّز (B+)</span> — حدّد سعر الجملة الخاص بهم.
                    {!form.wholesale_price && (
                      <span className="block mt-0.5 text-[#5C6E64]">إن تركته فارغاً سيرى الزبون B+ السعر العادي.</span>
                    )}
                  </p>
                  <label className="block text-xs font-bold text-[#5b21b6] mb-1">سعر الجملة (B+)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.wholesale_price}
                    onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })}
                    data-testid="form-wholesale-price"
                    className="w-full px-3 py-3 rounded-2xl border-2 border-[#7C3AED] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] bg-white text-base font-bold text-[#5b21b6]"
                    placeholder="مثال: 2500"
                  />

                  {/* Live B+ profit chip — wholesale_price minus cost_price.
                      Mirrors the A+ profit box (same colors / behavior). Stays
                      hidden until both wholesale + cost are provided so the
                      operator isn't shown an erroneous number. */}
                  {(() => {
                    const ws = parseFloat(form.wholesale_price || "");
                    const cp = parseFloat(form.cost_price || "");
                    const hasBoth = !isNaN(ws) && ws > 0 && !isNaN(cp) && cp >= 0;
                    if (!hasBoth) return null;
                    const profit = ws - cp;
                    const color = profit > 0 ? "#15803d" : profit < 0 ? "#E85D45" : "#5C6E64";
                    const bg    = profit > 0 ? "#DCFCE7" : profit < 0 ? "#FEE2E2" : "#F3F6F4";
                    const border = profit > 0 ? "#22C55E" : profit < 0 ? "#E85D45" : "#E4EBE6";
                    const marginPct = ((profit / ws) * 100).toFixed(1);
                    return (
                      <div className="mt-2" data-testid="form-profit-b-box">
                        <label className="block text-xs font-bold mb-1.5 flex items-center gap-1" style={{ color }}>
                          💎 ربح B+
                          <span className="text-[10px] font-normal opacity-80" dir="ltr">({marginPct}%)</span>
                        </label>
                        <div
                          className="w-full px-3 py-2.5 rounded-2xl border-2 text-base font-black text-center select-all"
                          style={{ backgroundColor: bg, borderColor: border, color }}
                          dir="ltr"
                          data-testid="form-profit-b-value"
                        >
                          {profit.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                          <span className="text-[10px] font-normal opacity-80 mr-1">د.ع</span>
                        </div>
                        {profit < 0 && (
                          <p className="text-[10px] text-[#E85D45] text-center mt-1 font-bold">
                            ⚠️ تنبيه: سعر الجملة أقل من سعر الشراء — خسارة على كل بيع
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {form.wholesale_price && form.price && Number(form.wholesale_price) > 0 && Number(form.price) > 0 && (
                    <p className="text-[10px] text-center mt-1.5 text-[#5b21b6]" dir="rtl">
                      الفرق عن سعر البيع:
                      <span className="font-bold mr-1" dir="ltr">
                        {(Number(form.price) - Number(form.wholesale_price)).toLocaleString("en-US")} د.ع
                      </span>
                      <span className="text-[#5C6E64] mr-1">
                        ({((Number(form.price) - Number(form.wholesale_price)) / Number(form.price) * 100).toFixed(1)}%)
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Cost Price + Selling Price + Profit + Stock — profit is a
                read-only green chip that updates live from price - cost. */}
            {(() => {
              const profit = (parseFloat(form.price || "0") || 0) - (parseFloat(form.cost_price || "0") || 0);
              const hasBoth = form.price !== "" && form.cost_price !== "";
              const profitColor = profit > 0 ? "#15803d" : profit < 0 ? "#E85D45" : "#5C6E64";
              const profitBg = profit > 0 ? "#DCFCE7" : profit < 0 ? "#FEE2E2" : "#F3F6F4";
              const profitBorder = profit > 0 ? "#22C55E" : profit < 0 ? "#E85D45" : "#E4EBE6";
              const marginPct = hasBoth && parseFloat(form.price) > 0
                ? ((profit / parseFloat(form.price)) * 100).toFixed(1)
                : null;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-[#111814] mb-2">سعر الشراء</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.cost_price}
                      onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                      data-testid="form-cost-price"
                      className="w-full px-3 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-sm"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111814] mb-2">سعر البيع *</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      data-testid="form-price"
                      className="w-full px-3 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-sm"
                      required
                    />
                  </div>
                  {/* Profit box — live computed, read-only, distinctive green */}
                  <div data-testid="form-profit-box">
                    <label className="block text-xs font-bold mb-2 flex items-center gap-1" style={{ color: profitColor }}>
                      💰 الربح
                      {marginPct != null && (
                        <span className="text-[10px] font-normal opacity-80" dir="ltr">({marginPct}%)</span>
                      )}
                    </label>
                    <div
                      className="w-full px-3 py-3 rounded-2xl border-2 text-base font-black text-center select-all"
                      style={{ backgroundColor: profitBg, borderColor: profitBorder, color: profitColor }}
                      dir="ltr"
                      data-testid="form-profit-value"
                    >
                      {hasBoth ? profit.toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111814] mb-2">الكمية *</label>
                    <input
                      type="number" min="0"
                      value={form.stock}
                      onChange={(e) => setForm({ ...form, stock: e.target.value })}
                      data-testid="form-stock"
                      className="w-full px-3 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-sm"
                      required
                    />
                  </div>
                </div>
              );
            })()}

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">القسم *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="form-category"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                required
              >
                <option value="">اختر القسم</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name_ar} - {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Brand (Company) — filtered by selected category */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                الشركة (Brand)
                {brands.length === 0 && form.category && (
                  <span className="text-xs text-[#5C6E64] mr-2">— لا توجد شركات في هذا القسم. أضف شركة من تبويبة "إدارة الشركات".</span>
                )}
              </label>
              <select
                value={form.brand_id}
                onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                data-testid="form-brand"
                disabled={!form.category}
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white disabled:opacity-50"
              >
                <option value="">بدون شركة</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name_ar} ({b.product_count} منتج)
                  </option>
                ))}
              </select>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                لون خلفية البطاقة
              </label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`color-${c}`}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      form.color === c
                        ? "border-[#2D4A3E] scale-110 shadow-md"
                        : "border-[#E4EBE6]"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Section Flags */}
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                يظهر في الأقسام التالية:
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 bg-[#F3F6F4] rounded-xl p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_offer}
                    onChange={(e) => setForm({ ...form, is_offer: e.target.checked, featured_offer_slot: e.target.checked ? form.featured_offer_slot : null })}
                    data-testid="form-is-offer"
                    className="w-5 h-5 accent-[#F5A623]"
                  />
                  <span className="text-sm font-medium">⚡ العروضات / الخصومات</span>
                </label>

                {/* Featured Offer Slot picker — only when is_offer is on.
                    Feb 13, 2026: expanded from 6 → 12 slots per admin request. */}
                {form.is_offer && (
                  <div className="bg-gradient-to-l from-[#FEF3C7] to-[#FDE68A] rounded-xl p-3 border-2 border-[#F5A623]/40" data-testid="form-featured-slot-picker">
                    <p className="text-sm font-bold text-[#7c2d12] mb-2 flex items-center gap-1">
                      ⭐ ترتيب العرض المميز
                      <span className="text-[10px] font-normal text-[#7c2d12]/70">(اختياري — رقم 1 يظهر أولاً عند الزبون)</span>
                    </p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => {
                        const active = Number(form.featured_offer_slot || 0) === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setForm({ ...form, featured_offer_slot: active ? null : n })}
                            data-testid={`form-featured-slot-${n}`}
                            className={`aspect-square rounded-full font-black text-base transition-all active:scale-90 ${
                              active
                                ? "bg-gradient-to-br from-[#F5A623] to-[#E85D45] text-white shadow-lg scale-110 ring-2 ring-[#7c2d12]/30"
                                : "bg-white text-[#7c2d12] border-2 border-[#F5A623]/40 hover:bg-[#FFF8E1]"
                            }`}
                            title={active ? "اضغط لإلغاء الموضع" : `وضع المنتج في الموضع رقم ${n}`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#7c2d12]/70 mt-2">
                      💡 12 خانة متاحة. لو اخترت رقماً ومنتج آخر موجود بنفس الرقم، يتم استبداله تلقائياً. الزبون لا يرى الأرقام — فقط المنتجات مرتّبة.
                    </p>
                  </div>
                )}
                <label className="flex items-center gap-3 bg-[#F3F6F4] rounded-xl p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_best_seller}
                    onChange={(e) => setForm({ ...form, is_best_seller: e.target.checked })}
                    data-testid="form-is-bestseller"
                    className="w-5 h-5 accent-[#2D4A3E]"
                  />
                  <span className="text-sm font-medium">⭐ الأكثر مبيعاً</span>
                </label>
                <label
                  className={`flex items-start gap-3 rounded-xl p-3 cursor-pointer border-2 transition-colors ${
                    form.excludes_rewards
                      ? "bg-[#FEE2E2] border-[#DC2626]"
                      : "bg-[#F3F6F4] border-transparent hover:border-[#DC2626]/30"
                  }`}
                  title="استثناء هذا المنتج من احتساب مكافأة الفئة (A+ 150 / B+ 50)"
                >
                  <input
                    type="checkbox"
                    checked={!!form.excludes_rewards}
                    onChange={(e) => setForm({ ...form, excludes_rewards: e.target.checked })}
                    data-testid="form-excludes-rewards"
                    className="w-5 h-5 accent-[#DC2626] mt-0.5"
                  />
                  <div className="text-sm leading-tight">
                    <p className="font-bold">🚫 استثناء من خصم/مكافأة الفئة</p>
                    <p className="text-[10px] text-[#5C6E64]">عند التفعيل: لا تُحسب 150 د.ع (A+) أو 50 د.ع (B+) لهذا المنتج عند إصدار الفاتورة.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#2D4A3E] font-medium rounded-full transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={submitting || uploading}
                data-testid="form-submit"
                className="flex-1 px-6 py-3 bg-[#2D4A3E] hover:bg-[#233A30] text-white font-bold rounded-full transition-colors disabled:opacity-50"
              >
                {submitting ? "جاري الحفظ..." : editProduct ? "حفظ التغييرات" : "إضافة المنتج"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
      {/* Image crop modal — appears on top */}
      <ImageCropModal
        file={pendingCrop?.file || null}
        aspect={1}
        onCancel={() => setPendingCrop(null)}
        onConfirm={handleCropConfirm}
      />
    </AnimatePresence>
  );
};

export default ProductFormModal;
